/**
 * Opcodes ocupam 1 byte e as faixas não se sobrepõem: cliente→servidor em **`0x00–0x7f`**,
 * servidor→cliente em **`0x80–0xff`**. Um frame que chega na direção errada cai no
 * `default` do dispatcher em vez de decodificar como outro pacote válido.
 *
 * A faixa do cliente era descrita como `0x0X` — um dígito só —, e isso **acabou** em
 * `Social 0x0f`: o décimo sexto opcode cliente é `Look 0x10`, e a leitura literal do
 * comentário antigo o poria fora da faixa (PRD §15.4). O que separa as direções sempre foi
 * o bit alto, e é ele que o dispatcher checa; o dígito único era uma coincidência dos
 * quinze primeiros. A correção é do comentário, não do código — mas ficar com a decisão de
 * §4 registrada de um jeito e o código de outro é como um contrato vira folclore.
 */
export const ClientOp = {
    Move: 0x01,
    Ping: 0x02,
    Attack: 0x03,
    /** Primeiro frame da conexão: apresenta o token de sessão emitido pelo Login Server. */
    Auth: 0x04,
    /** Fala local, que vira balão sobre o personagem. */
    Chat: 0x05,
    /** Conjura uma magia do catálogo compartilhado. */
    Cast: 0x06,
    /** Move um item entre inventário, equipamento e chão — inclui pegar e soltar. */
    MoveItem: 0x07,
    /** Abre ou fecha um contêiner do inventário. */
    Container: 0x08,
    /** Usa um item: comer, vestir pelo clique, abrir baú. */
    UseItem: 0x09,
    /**
     * Usa um item **em alguma coisa**: runa numa criatura, runa num ponto do chão.
     *
     * Opcode próprio, e não campos novos em `UseItem`, porque os dois gestos são diferentes
     * para quem joga: beber uma poção não pede mira, e usar uma runa não faz sentido sem
     * ela. Um pacote que aceitasse alvo opcional obrigaria o servidor a adivinhar qual dos
     * dois o cliente quis dizer quando o alvo viesse zerado.
     */
    UseOn: 0x0a,
    /** Fala com um NPC: abre a loja, o banco, ou só o que ele tem a dizer. */
    Interact: 0x0b,
    /** Compra ou venda com o NPC comerciante já aberto. */
    Shop: 0x0c,
    /** Depósito, saque ou transferência no banco. */
    Bank: 0x0d,
    /** Troca entre jogadores, em duas etapas. */
    Trade: 0x0e,
    /** Lista de amigos e guildas. */
    Social: 0x0f,
    /**
     * Olha um tile: "o que há aqui?" (PRD §15.4).
     *
     * Sete bytes, e **zero opcode de servidor** — a resposta cabe no `ServerMessage 0x88`,
     * cujo próprio comentário já usava "você vê uma espada" como exemplo de `MessageKind.Info`.
     * O pacote foi escrito prevendo este.
     *
     * É a melhor razão custo/benefício desta seção inteira: sem olhar, um mundo com nome em
     * quarenta lugares, placa, livro e item com descrição é um mundo cujo texto ninguém lê.
     */
    Look: 0x10,
    /**
     * Age sobre um tile **sem item na mão**: alavanca, baú, porta.
     *
     * Opcode próprio porque nenhum dos que havia alcança o gesto. `Look` só olha. `UseOn 0x0a`
     * exige item do inventário — alavanca e baú não são runa. `UseItem 0x09` endereça
     * `container` e `slot`, então nunca alcança um baú do mapa.
     *
     * A alternativa era um sentinela de "mão vazia" em `UseOn`, e ela foi recusada pelo mesmo
     * argumento que separou `UseOn` de `UseItem`: um pacote que aceita alvo opcional obriga o
     * servidor a adivinhar qual dos dois gestos o cliente quis dizer quando o campo vier zerado.
     */
    UseTile: 0x11,
    /** Abre o bestiário (PRD §15.5). Sem corpo: o servidor já sabe de quem é a contagem. */
    Bestiary: 0x12,
    /**
     * Escolhe a postura de combate: ataque, equilíbrio ou defesa (PRD §5).
     *
     * Opcode próprio, e não um campo a mais no `Attack 0x03`, porque as duas coisas mudam em
     * ritmos diferentes: o alvo troca a cada criatura e a postura fica escolhida por sessões
     * inteiras. Grudadas no mesmo pacote, mudar de postura exigiria reenviar o alvo — e um
     * clique no botão com o alvo já morto viraria uma seleção inválida sem que ninguém tivesse
     * pedido nada a respeito de alvo.
     */
    Stance: 0x13,
    /**
     * Escolhe o que o auto-loot recolhe sozinho do corpo da criatura abatida (PRD §6).
     *
     * Opcode próprio, e não uma flag em `UseItem`, pelo mesmo argumento que separou a postura
     * do ataque: a lista muda por sessões inteiras e o gesto de saquear acontece a cada abate.
     * Grudadas, mudar a lista exigiria mandar um gesto de saque junto — e a lista é justamente
     * o que existe para **não** haver gesto.
     *
     * Manda a lista **inteira** a cada mudança, e não "acrescente este item": são dezenas de
     * entradas de dois bytes, o pacote sai por clique numa caixa de seleção, e um delta traria
     * a única pergunta que ele não sabe responder — o que fazer quando o cliente e o servidor
     * discordam sobre o que havia antes.
     */
    LootFilter: 0x14,
    /**
     * Liga, desliga e ajusta as automações que não são o auto-loot (PRD §6).
     *
     * Opcode próprio, e não campos novos no `LootFilter 0x14`, pelo mesmo corte que separou os
     * dois de `Attack` e `UseItem`: o auto-loot é uma **lista de itens** que muda quando o
     * jogador descobre um item novo, e isto é um punhado de interruptores e réguas que mudam
     * quando ele decide como vai caçar hoje. Grudados, marcar uma escama de dragão reenviaria a
     * rota inteira, e ligar a auto-comida reenviaria as sessenta e quatro marcações.
     *
     * O corpo é **assimétrico** com o do servidor, ao contrário de todos os pares anteriores, e
     * o comentário de `packets/automation.ts` explica por quê: a rota é gravada pelo servidor,
     * porque só ele vê o personagem em cima do degrau da escada.
     */
    Automation: 0x15,
};
export const ServerOp = {
    Welcome: 0x81,
    Snapshot: 0x82,
    Despawn: 0x83,
    Pong: 0x84,
    Combat: 0x85,
    /** Estado privado do próprio jogador: nível, mana, experiência, skills. */
    Stats: 0x86,
    /** Nome de uma entidade que acabou de entrar na Área de Interesse. */
    EntityInfo: 0x87,
    /** Texto do servidor para um jogador só. */
    ServerMessage: 0x88,
    /** Fala de alguém, com autor e posição. */
    Chat: 0x89,
    /** Conteúdo de equipamento e contêineres abertos. */
    Inventory: 0x8a,
    /** Pilhas de item visíveis no chão. */
    GroundItems: 0x8b,
    /** Efeito visual: explosão, projétil, cura. Puramente cosmético. */
    Effect: 0x8c,
    /** Um pedaço do terreno. Substitui o mapa inteiro que o Welcome carregava. */
    MapChunk: 0x8d,
    /** Você está na fila desta região. Chega no lugar do Welcome enquanto ela estiver cheia. */
    Queue: 0x8e,
    /** Vitrine de um NPC comerciante: o que ele vende, o que compra e por quanto. */
    Shop: 0x8f,
    /** Saldo no banco. */
    Bank: 0x90,
    /** Estado da troca entre dois jogadores. */
    Trade: 0x91,
    /** Lista de amigos e membros da guilda, com presença. */
    Social: 0x92,
    /** Continue noutra porta: a região de destino é outro processo (PRD §3.4). */
    Redirect: 0x93,
    /**
     * Um tile mudou depois de o chunk dele já ter viajado (PRD §15.2).
     *
     * Contraria a decisão de §3.5 — "terreno é estático, cada chunk viaja uma vez por sessão
     * e nunca é reenviado" —, e a decisão estava certa **para um mundo sem porta, sem
     * alavanca, sem buraco de pá e sem rocha quebrável**. Sem nenhum desses o mapa é
     * fotografia, não máquina.
     *
     * O que **não** muda é a razão daquela decisão. Reenviar o `MapChunk` inteiro por causa
     * de uma porta custaria 256 bytes onde a informação são três coordenadas e um byte, e o
     * defeito só apareceria com dez jogadores atravessando um corredor de portas. O chunk
     * continua viajando uma vez; o que muda depois viaja em sete bytes, e só para quem já
     * recebeu aquele chunk.
     */
    TileUpdate: 0x94,
    /** A ficha do bestiário, inteira. Ver `packets/bestiary.ts`. */
    Bestiary: 0x95,
    /**
     * A lista de auto-loot que o servidor guarda para este personagem.
     *
     * Sai uma vez, no `join`, e é o que faz a lista sobreviver ao logout: ela mora no
     * personagem e não na máquina, porque quem caça com ela é o personagem — quem entra do
     * computador do trabalho não quer recomeçar a marcar itens.
     */
    LootFilter: 0x96,
    /**
     * O estado das automações deste personagem, rota inclusa (PRD §6).
     *
     * Sai no `join`, pelo mesmo motivo do `LootFilter 0x96` — a escolha mora no personagem e
     * não na máquina —, e **também a cada mudança da rota**, que é onde ele difere: a lista de
     * auto-loot só muda quando o jogador clica, e a rota cresce sozinha enquanto ele anda com a
     * gravação ligada. Sem o reenvio, a tela mostraria "0 pontos" durante a volta inteira.
     */
    Automation: 0x97,
};
//# sourceMappingURL=opcodes.js.map